import { Close, Search } from '@mui/icons-material';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { StyleObj } from '../../@types';
import { CasinoCategoryRow } from '../../@types/casino';
import type { EnumValues } from '../../@types/utils';
import { QUERY_KEYS } from '../../constants';
import API_ROUTES from '../../constants/apiRoutes';
import { buildCasinoCategoryUpdatePayload } from '../../helpers/casino';
import { useCasinoGamesTable } from '../../hooks/useCasinoGamesTable';
import useMutateData from '../../hooks/useMutateData';
import { useCasinoCategories, useCasinoProviders } from '../../queries/casino';
import { mergeSx } from '../../utils/styles';
import PositionInput from '../atoms/PositionInput';
import FormModalLayout from '../layouts/FormModalLayout';
import {
  createSelectedGameIdSet,
  createSelectedGamePositionMap,
  getCategoryGameIds,
  reorderSelectedGameIds,
  toggleSelectedGameId,
} from './casinoCategoryGamesModal.utils';

type CasinoCategoryGamesModalProps = {
  category: CasinoCategoryRow;
  closeModal?: () => void;
};

const GAME_SELECTION_FILTER = {
  ALL: 'all',
  SELECTED: 'selected',
  UNSELECTED: 'unselected',
};

type GameSelectionFilter = EnumValues<typeof GAME_SELECTION_FILTER>;
type GamePositionDrafts = Record<string, number | null>;

const styles: StyleObj = {
  content: {
    px: 3,
    pb: 3,
    gap: 2,
  },
  controls: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: {
      xs: 'stretch',
      md: 'center',
    },
    gap: 1,
  },
  search: {
    flex: 1,
  },
  providerSelect: {
    minWidth: {
      xs: '100%',
      md: 220,
    },
  },
  listWrap: {
    border: (theme) => `1px solid ${theme.palette.divider}`,
    borderRadius: 1,
    overflow: 'hidden',
  },
  listScroll: {
    minHeight: {
      xs: 360,
      md: 440,
    },
    maxHeight: {
      xs: 420,
      md: 520,
    },
    overflow: 'auto',
  },
  list: {
    py: 0,
  },
  listItem: {
    position: 'relative',
    pl: {
      xs: 0.5,
      md: 1.5,
    },
    pr: {
      xs: 0.5,
      md: 1.5,
    },
    py: {
      xs: 0,
      md: 0.5,
    },
    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
  },
  selectedListItem: {
    bgcolor: 'action.selected',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: 2,
      bgcolor: 'primary.main',
    },
    '&:hover': {
      bgcolor: 'action.hover',
    },
  },
  listItemButton: {
    flex: 1,
    p: 0,
    '&:hover': {
      bgcolor: 'transparent',
    },
  },
  checkbox: {
    p: {
      xs: 0,
      md: 0.25,
    },
    mr: {
      xs: 0.5,
      md: 1.25,
    },
  },
  gameMeta: {
    display: 'block',
  },
  positionInput: {
    ml: 'auto',
    width: 100,
    mr: 1,
  },
  emptyState: {
    minWidth: {
      xs: 0,
    },
    minHeight: 240,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    px: 3,
  },
  pagination: {
    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
  },
};

const CasinoCategoryGamesModal = ({ category, closeModal }: CasinoCategoryGamesModalProps) => {
  const {
    data: casinoCategories = [],
    isFetching: categoriesFetching,
    isLoading: categoriesLoading,
  } = useCasinoCategories();
  const categoryGameIds = getCategoryGameIds(category, casinoCategories);
  const isResolvingCategoryGameIds = categoriesFetching || categoriesLoading;
  const canManageCategoryGames = Array.isArray(categoryGameIds);
  const categorySelectedGameIds = useMemo(() => createSelectedGameIdSet(categoryGameIds), [categoryGameIds]);
  const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(categorySelectedGameIds);
  const selectedGamePositions = useMemo(() => createSelectedGamePositionMap(selectedGameIds), [selectedGameIds]);
  const [gamePositionDrafts, setGamePositionDrafts] = useState<GamePositionDrafts>({});
  const recentPositionCommitGameIdRef = useRef<string | null>(null);
  const [search, setSearch] = useState('');

  const {
    data: casinoGamesData,
    isFetching,
    isLoading,
    updateQueryParams,
    changeQuery,
    handleSearch,
    queryParams,
  } = useCasinoGamesTable({
    initialQueryParams: {
      categoryId: undefined,
    },
  });
  const { data: providers = [], isLoading: providersLoading } = useCasinoProviders();
  const [providerName, setProviderName] = useState(queryParams.providerName ?? '');
  const [gameSelectionFilter, setGameSelectionFilter] = useState<GameSelectionFilter>('all');
  const games = casinoGamesData?.items ?? [];
  const isBusy = isFetching || isLoading;

  const { updateData } = useMutateData(
    API_ROUTES.CASINO.CATEGORY_UPDATE,
    [QUERY_KEYS.casinoCategories, QUERY_KEYS.casinoGames],
    'casino'
  );

  useEffect(() => {
    setSelectedGameIds(categorySelectedGameIds);
  }, [categorySelectedGameIds]);

  useEffect(() => {
    setProviderName(queryParams.providerName ?? '');
  }, [queryParams.providerName]);

  const handleClose = () => {
    closeModal?.();
  };

  const handleSave = () => {
    updateData(
      category.id,
      buildCasinoCategoryUpdatePayload({
        gameIds: Array.from(selectedGameIds),
      }),
      handleClose,
      'Category games updated successfully.'
    );
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextSearch = event.target.value;

    setSearch(nextSearch);
    handleSearch(nextSearch.trim() || null);
  };

  const handleClearSearch = () => {
    setSearch('');
    handleSearch(null);
  };

  const handleProviderChange = (event: SelectChangeEvent) => {
    const nextProviderName = event.target.value;

    setProviderName(nextProviderName);
    changeQuery({
      providerName: nextProviderName || undefined,
    });
  };

  const handleGameSelectionFilterChange = (event: SelectChangeEvent) => {
    const nextGameSelectionFilter = event.target.value as GameSelectionFilter;
    const showAllGames = nextGameSelectionFilter === GAME_SELECTION_FILTER.ALL;

    setGameSelectionFilter(nextGameSelectionFilter);
    changeQuery({
      categoryId: showAllGames ? undefined : category.id,
      categoryFilter: showAllGames ? undefined : nextGameSelectionFilter,
      sortGames: nextGameSelectionFilter === GAME_SELECTION_FILTER.SELECTED ? true : undefined,
    });
  };

  const handlePageChange = (_event: unknown, page: number) => {
    updateQueryParams({
      page,
      pageSize: queryParams.limit,
    });
  };

  const handleRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateQueryParams({
      page: 0,
      pageSize: Number(event.target.value),
    });
  };

  const handleGameToggle = (gameId: string) => {
    if (recentPositionCommitGameIdRef.current === gameId) {
      recentPositionCommitGameIdRef.current = null;
      return;
    }

    const draftPosition = gamePositionDrafts[gameId];

    setSelectedGameIds((prevValue) => {
      if (prevValue.has(gameId) || draftPosition == null) {
        return toggleSelectedGameId(prevValue, gameId);
      }

      return reorderSelectedGameIds(prevValue, gameId, draftPosition);
    });
  };

  const handleGamePositionChange = (gameId: string, position: number | null) => {
    recentPositionCommitGameIdRef.current = gameId;
    setSelectedGameIds((prevValue) => reorderSelectedGameIds(prevValue, gameId, position));
    setGamePositionDrafts((prevValue) => {
      const { [gameId]: _draft, ...nextValue } = prevValue;

      return nextValue;
    });

    window.setTimeout(() => {
      if (recentPositionCommitGameIdRef.current === gameId) {
        recentPositionCommitGameIdRef.current = null;
      }
    }, 0);

    return Promise.resolve();
  };

  const handleGamePositionDraftChange = (gameId: string, position: number | null) => {
    setGamePositionDrafts((prevValue) => ({
      ...prevValue,
      [gameId]: position,
    }));
  };

  return (
    <FormModalLayout
      onSave={handleSave}
      onClose={handleClose}
      label={`Manage games for ${category.name} (${selectedGameIds.size})`}
      showEditTabs={false}
      disablePadding
      inputSpacing={0}
      disableSubmit={isResolvingCategoryGameIds || !canManageCategoryGames}
    >
      <Stack sx={styles.content}>
        {!isResolvingCategoryGameIds && !canManageCategoryGames && (
          <Alert severity='warning'>
            Assigned game IDs are unavailable for this category, so changes cannot be saved safely.
          </Alert>
        )}

        <Stack sx={styles.controls}>
          <TextField
            placeholder='Search games'
            value={search}
            onChange={handleSearchChange}
            sx={styles.search}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search fontSize='small' />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position='end'>
                    <IconButton aria-label='Clear search' onClick={handleClearSearch} edge='end' size='small'>
                      <Close fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormControl sx={styles.providerSelect} disabled={providersLoading && !providers.length}>
            <InputLabel id='casino-category-games-provider-filter-label'>Provider</InputLabel>
            <Select
              labelId='casino-category-games-provider-filter-label'
              label='Provider'
              value={providerName}
              onChange={handleProviderChange}
            >
              <MenuItem value=''>All providers</MenuItem>
              {providers.map((provider) => (
                <MenuItem key={provider._id} value={provider.name}>
                  {provider.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={styles.providerSelect}>
            <InputLabel id='casino-category-games-selected-filter-label'>Selection</InputLabel>
            <Select
              labelId='casino-category-games-selected-filter-label'
              label='Selection'
              value={gameSelectionFilter}
              onChange={handleGameSelectionFilterChange}
            >
              <MenuItem value={GAME_SELECTION_FILTER.ALL}>All games</MenuItem>
              <MenuItem value={GAME_SELECTION_FILTER.SELECTED}>Selected</MenuItem>
              <MenuItem value={GAME_SELECTION_FILTER.UNSELECTED}>Unselected</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Box sx={styles.listWrap}>
          <Box sx={styles.listScroll}>
            {isBusy ? (
              <Stack sx={styles.emptyState}>
                <CircularProgress size={28} />
              </Stack>
            ) : games.length ? (
              <List disablePadding sx={styles.list}>
                {games.map((game) => {
                  const checked = selectedGameIds.has(game.id);

                  return (
                    <Stack
                      key={game.id}
                      direction='row'
                      sx={mergeSx(
                        { alignItems: 'center' },
                        styles.listItem,
                        checked ? styles.selectedListItem : undefined
                      )}
                    >
                      <ListItemButton onClick={() => handleGameToggle(game.id)} sx={styles.listItemButton}>
                        <Checkbox checked={checked} tabIndex={-1} disableRipple size='small' sx={styles.checkbox} />
                        <ListItemText
                          primary={game.name}
                          secondary={
                            <Box component='span' sx={styles.gameMeta}>
                              {game.providerName}
                              {game.categoryName ? ` - ${game.categoryName}` : ''}
                            </Box>
                          }
                        />
                      </ListItemButton>
                      <PositionInput
                        value={selectedGamePositions.get(game.id) ?? null}
                        onCommit={(position) => handleGamePositionChange(game.id, position)}
                        onValueChange={(position) => handleGamePositionDraftChange(game.id, position)}
                        preserveDirtyValue
                        sx={styles.positionInput}
                      />
                    </Stack>
                  );
                })}
              </List>
            ) : (
              <Stack sx={styles.emptyState}>
                <Typography variant='body2' color='text.secondary'>
                  No games found
                </Typography>
              </Stack>
            )}
          </Box>

          <TablePagination
            component='div'
            count={casinoGamesData?.count ?? 0}
            page={queryParams.page - 1}
            rowsPerPage={queryParams.limit}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            sx={styles.pagination}
          />
        </Box>
      </Stack>
    </FormModalLayout>
  );
};

export default CasinoCategoryGamesModal;
